/**
 * Frontend mirror of the manual test-run DTOs (NEX-508). The backend exposes a
 * fully nested {@link TestRunDTO}; list endpoints return the slim shape with
 * {@code caseResults}/{@code stepResults} set to {@code null}.
 */

import type { ProviderType } from './project-integration.model';
import type { DefectRef } from './defect-ref.model';
import type { ExecutionJobStatus } from '../services/execution-job.service';

/** Result status of a run, case result or step result. */
export const RESULT_STATUSES = ['NOT_RUN', 'RUNNING', 'PASSED', 'FAILED', 'BLOCKED', 'SKIPPED'] as const;
export type ResultStatus = (typeof RESULT_STATUSES)[number];

/** Kind of a resolved step-result node within the flat step tree. */
export const STEP_RESULT_KINDS = ['STEP', 'MODULE', 'ITERATION', 'CHECK'] as const;
export type StepResultKind = (typeof STEP_RESULT_KINDS)[number];

/** Vollstaendige Spiegelung des Backend-Enums `TestRunSource.java`. */
export type TestRunSource = 'TESTOFFICE' | 'MANUAL' | 'ROBOT' | 'JUNIT' | 'PIPELINE' | 'TCDL' | 'IMPORT';
export type TestRunType = 'MANUAL' | 'SMOKE' | 'REGRESSION' | 'INTEGRATION' | 'PERFORMANCE';

/** Spiegel des Backend-Enums `PipelineFehlerKategorie.java` (NEX-1548). */
export const PIPELINE_FEHLER_KATEGORIEN = [
  'BUILD',
  'ABHAENGIGKEIT',
  'INFRASTRUKTUR',
  'KONFIGURATION',
  'ERGEBNIS_FEHLT',
  'TESTFEHLER',
  'ABGEBROCHEN',
] as const;
export type PipelineFehlerKategorie = (typeof PIPELINE_FEHLER_KATEGORIEN)[number];

/**
 * Warum der CI-Lauf einer Runde nicht gruen war (NEX-1548). Bei Erfolg, von Hand und solange die
 * Runde laeuft fehlt der Befund. Optional, weil nicht jede Antwort, die diese Form fuellt, die
 * Felder schon traegt.
 */
export interface PipelineFehlerBefund {
  fehlerKategorie?: PipelineFehlerKategorie | null;
  fehlerJob?: string | null;
  fehlerSchritt?: string | null;
  fehlerZeile?: number | null;
  /** false: Runde ohne Testergebnisse gescheitert, zaehlt nicht in der Pass-Rate. */
  aussagekraeftig?: boolean;
}

/** Lifecycle status of a run — distinct from the pass/fail {@link ResultStatus}. */
export const TEST_RUN_STATUSES = ['DRAFT', 'READY', 'RUNNING', 'PAUSED', 'COMPLETED', 'ABORTED'] as const;
export type TestRunStatus = (typeof TEST_RUN_STATUSES)[number];

/** Request to create a bug from a failed step result (NEX-776/781); all fields optional. */
export interface CreateBugFromStepResultRequest {
  subject?: string;
  description?: string;
  /** Redmine tracker id; null uses the provider default. */
  trackerId?: number;
  /** Redmine status id; null uses the provider default. */
  statusId?: number;
  /** Ziel-Verbindung des Projekts; fehlt sie, waehlt der Server die erste aktive (NEX-1204). */
  integrationId?: string;
}

/** Bug created from a step result in the active provider (NEX-776/781). */
export interface CreatedBug {
  providerType: ProviderType;
  /** Stable external identifier (Jira issue key, Redmine numeric id). */
  key: string;
  /** Server-built deep link to the created issue. */
  browseUrl: string;
  /** False when the issue was created and linked but evidence upload failed. */
  attachmentsUploaded: boolean;
}

export interface TestStepResult {
  id: string;
  testCaseResultId: string;
  /** Parent MODULE node id; null for top-level steps. Drives the resolved tree. */
  parentStepResultId: string | null;
  testStepId: string | null;
  kind: StepResultKind;
  orderIndex: number;
  iterationIndex: number | null;
  /**
   * Zeile des Datenblatt-Durchgangs am Modulknoten (NEX-1090); gilt fuer dessen Schritte.
   * Optional, weil aeltere Laeufe und Robot-Importe das Feld nicht tragen.
   */
  iterationValues?: Record<string, string> | null;
  descriptionSnapshot: string | null;
  expectedSnapshot: string | null;
  actualResult: string | null;
  status: ResultStatus;
  comment: string | null;
  /** Jira key of the bug linked to this step result (NEX-530); null when unlinked. */
  linkedBugJiraKey: string | null;
  executedAt: string | null;
  /** Derived elapsed seconds; null where not derivable (skipped step, group header). */
  durationSeconds: number | null;
  createdAt: string;
  updatedAt: string;
  /** Liefert das Backend am Testlauf noch nicht mit; bis dahin traegt nur ein frisch erfasster Fehler seinen Key hier ein. */
  defects?: DefectRef[];
}

export interface TestCaseResult {
  id: string;
  testRunId: string;
  testCaseId: string;
  orderIndex: number;
  caseTitleSnapshot: string | null;
  caseKeySnapshot: string | null;
  status: ResultStatus;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Flat list in {@code orderIndex} order; {@code null} on the slim shape. */
  stepResults: TestStepResult[] | null;
}

export interface TestRun extends PipelineFehlerBefund {
  id: string;
  key: string;
  projectId: string;
  name: string | null;
  source: TestRunSource;
  runType: TestRunType;
  environment: string | null;
  /**
   * Fachliche Release-ID des Laufs (NEX-1450); `null` bei Läufen aus der Zeit vor der
   * Release-Pflicht. Optional, weil nicht jede Antwort, die diese Form füllt, das Feld schon trägt.
   */
  releaseId?: string | null;
  status: TestRunStatus;
  /** Aggregated pass/fail outcome of the run; {@code null} until first decided. */
  outcome: ResultStatus | null;
  executedByUserId: string | null;
  /** Resolved display name of the executor; null when unresolved. */
  executedByName: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /** Computed run duration in seconds (completedAt − startedAt); null until completed. */
  durationSeconds: number | null;
  createdAt: string;
  updatedAt: string;
  /** Fully nested on the detail/create shape; {@code null} on the list shape. */
  caseResults: TestCaseResult[] | null;
  /**
   * Tests des Reports im neuesten Durchlauf, die keinem Testfall zugeordnet wurden.
   * Optional, weil nicht jedes Backend das Feld schon liefert.
   */
  unmatchedTests?: number;
  /**
   * Begruendung eines Abbruchs (NEX-1314/1315); {@code null}, solange der Lauf nicht
   * abgebrochen ist, und bei einem Abbruch aus der Zeit vor der Pflicht-Begruendung.
   * Optional, weil nicht jede Antwort, die diese Form fuellt, das Feld schon traegt.
   */
  abortReason?: string | null;
  abortedByUserId?: string | null;
  abortedBySystem?: boolean;
}

export interface TestStepResultAttachment {
  id: string;
  testStepResultId: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  createdBy: string;
  createdAt: string;
}

/** Request body for recording a step result. */
export interface UpdateStepResultRequest {
  status: ResultStatus;
  actualResult?: string;
  comment?: string;
  /** Empty string removes the link; omit to leave unchanged (NEX-530). */
  linkedBugJiraKey?: string;
}

/** Woraus ein Testlauf entsteht (NEX-1035). Bestimmt, welche Felder gefuellt sein muessen. */
export const TEST_RUN_SOURCE_TYPES = ['PLAN', 'CASES', 'REPO'] as const;
export type TestRunSourceType = (typeof TEST_RUN_SOURCE_TYPES)[number];

/** Was nach dem Anlegen zu tun ist (NEX-1035) — der Server entscheidet, der Client folgt. */
export const TEST_RUN_NEXT_ACTIONS = ['RUN_MANUAL', 'TRIGGER_PIPELINE', 'RUN_ON_RUNNER'] as const;
export type TestRunNextAction = (typeof TEST_RUN_NEXT_ACTIONS)[number];

/** Zielwert einer Datenblatt-Zuordnung, die fuer den gesamten Lauf gilt. */
export const DATA_SHEET_TARGET_ALL = '__all';

/**
 * Zuordnung eines Testdatenblatts innerhalb des Laufs (NEX-1035).
 *
 * @see DATA_SHEET_TARGET_ALL fuer den gesamten Lauf; sonst die ID eines Plans
 *      oder Testfalls aus der Auswahl.
 */
export interface DataSheetAssignment {
  id: string;
  target: string;
}

/** Verweis auf die Pipeline eines verbundenen Repositories (NEX-1035). */
export interface RepositoryPipelineRef {
  repositoryId: string;
  pipeline: string;
}

/**
 * Anlegen eines oder mehrerer Testlaeufe (NEX-1035/1036). Aus {@code PLAN}
 * entsteht ein Lauf pro Plan, aus {@code CASES} und {@code REPO} genau einer.
 */
export interface CreateTestRunRequest {
  kind: RunKind;
  sourceType: TestRunSourceType;
  /** Fachliche Release-ID; Pflicht für jeden Lauf mit `sourceType`. */
  releaseId: string;
  planIds?: string[];
  caseIds?: string[];
  repositoryPipeline?: RepositoryPipelineRef;
  dataSheets?: DataSheetAssignment[];
  environment?: string;
  owner?: string;
  tags?: string[];
  note?: string;
  name?: string;
  /** Ziel eines automatisierten Laufs aus Plänen oder Testfällen (NEX-1750); nie zusammen mit `requiredLabels`. */
  runnerId?: string;
  requiredLabels?: string[];
}

/** Ergebnis des Anlegens (NEX-1035): der neue Lauf und was als naechstes zu tun ist. */
export interface CreatedTestRun {
  id: string;
  key: string;
  name: string | null;
  /**
   * Der beim Anlegen geoeffnete Durchlauf. `null` bei einer Repository-Quelle: dort
   * entsteht der Durchlauf erst mit dem Import der Pipeline-Ergebnisse.
   */
  executionId: string | null;
  nextAction: TestRunNextAction;
}

/**
 * Kopfdaten und Zusammenstellung eines angelegten Laufs zum Vorbefuellen des
 * Bearbeiten-Formulars (NEX-1100, `GET …/test-runs/{runId}/settings`).
 */
export interface TestRunSettings {
  id: string;
  key: string;
  name: string;
  status: TestRunStatus;
  kind: RunKind;
  /** `null` bei importierten Laeufen und Bestandslaeufen ohne Quelle. */
  sourceType: TestRunSourceType | null;
  releaseId: string | null;
  environment: string | null;
  owner: string | null;
  tags: string[];
  note: string | null;
  planIds: string[];
  caseIds: string[];
  repositoryPipeline: RepositoryPipelineRef | null;
  dataSheets: DataSheetAssignment[];
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Aenderung der Kopfdaten eines Laufs (NEX-1100, `PATCH …/test-runs/{runId}`).
 * Art, Release und Zusammenstellung sind nach dem Anlegen nicht aenderbar und
 * fehlen deshalb hier. `owner: null` lässt den Verantwortlichen stehen.
 */
export interface UpdateTestRunRequest {
  name: string;
  environment: string;
  owner: string | null;
  tags: string[];
  note: string;
}

/** Art eines Testlaufs (NEX-1033) — manuell durchgeklickt oder maschinell ausgefuehrt. */
export const RUN_KINDS = ['MANUAL', 'AUTOMATED'] as const;
export type RunKind = (typeof RUN_KINDS)[number];

/** Umgebungen mit eigener Gestaltung; die Spalte selbst bleibt Freitext. */
export const TEST_RUN_ENVIRONMENTS = ['TEST', 'STAGING', 'PRODUCTION', 'LOCAL'] as const;
export type TestRunEnvironment = (typeof TEST_RUN_ENVIRONMENTS)[number];

/** Eine Zeile der Testlauf-Uebersicht (NEX-1033). */
export interface TestRunListItem {
  id: string;
  key: string;
  title: string;
  art: RunKind;
  environment: string | null;
  status: ResultStatus | null;
  stepsPassed: number;
  stepsTotal: number;
  durationMs: number | null;
  assigneeUserId: string | null;
  assignee: string | null;
  /**
   * Woher der Lauf stammt (NEX-1332). Traegt die Uebersicht, weil sie den Bearbeiter danach
   * beschriftet: ein Pipeline-Lauf gehoert der CI, ein manueller ohne zugewiesene Person
   * niemandem — am fehlenden `assignee` allein ist das nicht zu unterscheiden.
   */
  source: TestRunSource;
  updatedAt: string;
  /**
   * Lebenszyklus neben dem Ergebnis in `status` (NEX-1400): nur an ihm ist ein
   * abgebrochener Lauf von einem uebersprungenen zu unterscheiden.
   */
  runStatus: TestRunStatus;
  /** Nur bei einem abgebrochenen Lauf gesetzt; aeltere Abbrueche tragen keine. */
  abortReason: string | null;
}

/**
 * Projektweite Kennzahlen der Testlauf-Uebersicht (NEX-1081).
 *
 * Alle Zahlen gelten fuer die gesamte gefilterte Menge, nicht fuer die geladene
 * Seite - die Uebersicht blaettert serverseitig. `notRun` enthaelt auch Laeufe
 * ohne Ergebnis; genau so zeigt die Zeile sie an.
 */
export interface TestRunStats {
  total: number;
  notRun: number;
  running: number;
  passed: number;
  failed: number;
  blocked: number;
  skipped: number;
}

/** Filterkriterien der Uebersicht; leere Werte werden nicht mitgesendet. */
export interface TestRunListFilter {
  query?: string;
  art?: RunKind | '';
  status?: ResultStatus | '';
  environment?: string;
  assignee?: string;
  /** Lebenszyklus statt Ergebnis, etwa nur abgebrochene Laeufe (NEX-1400). */
  runStatus?: TestRunStatus | '';
}

/**
 * Eine Durchfuehrung eines Testlaufs (NEX-1037). Ein Lauf kann mehrfach
 * durchgefuehrt werden; jede Wiederholung ist ein eigener Durchlauf, damit die
 * Uebersicht bei einer Zeile pro Lauf bleibt.
 *
 * `live` liefert der Server abgeleitet aus `status === 'RUNNING'` — kein eigenes
 * Feld in der Datenbank. `durationMs` und `runningSince` schliessen sich aus und
 * fehlen jeweils in der Antwort, solange sie nicht gelten.
 */
export interface TestRunExecution extends PipelineFehlerBefund {
  id: string;
  nr: number;
  startedAt: string;
  status: ResultStatus;
  live: boolean;
  stepsPassed: number;
  stepsTotal: number;
  durationMs?: number;
  runningSince?: string;
  assigneeUserId: string | null;
  assigneeName: string | null;
  commentCount: number;
  /** Anbieter des am Durchlauf verknuepften Bugs; fehlt, solange keiner haengt. */
  linkedBugProviderType?: ProviderType | null;
  /** Kennung des am Durchlauf verknuepften Bugs; fehlt, solange keiner haengt. */
  linkedBugKey?: string | null;
  /** Nummer des Durchlaufs, den dieser Retest wiederholt; fehlt bei einem normalen Durchlauf (NEX-1757). */
  retestOfNr?: number;
  /** Testfaelle, die ein Retest dieses Durchlaufs wiederholen wuerde; 0 bei laufenden. */
  retestCaseCount?: number;
}

/** Was die Bug-Anlage an einem Durchlauf an den Vorgang haengt: Kategorie, Job, Schritt und Zeile. */
export interface ExecutionBugPreview {
  errorSection: string | null;
}

/** Dieselbe Antwort liefert die Vorschau am Fall-Ergebnis. */
export type BugPreview = ExecutionBugPreview;

/** Antwort auf das Starten eines Durchlaufs (NEX-1037). */
export interface StartedTestRunExecution {
  executionId: string;
  nr: number;
  nextAction: TestRunNextAction;
  /** Auftrag fuer den gewaehlten Rechner; gesetzt genau bei `RUN_ON_RUNNER`. */
  jobId: string | null;
}

/**
 * Was das Anstossen einer Pipeline zurueckmeldet (NEX-752).
 *
 * `executionId` nennt den Durchlauf, den dieses Ausloesen angelegt hat — jedes Ausloesen
 * bekommt seinen eigenen, und genau den oeffnet die Oberflaeche danach.
 */
export interface TriggeredPipeline {
  runId: string;
  runKey: string | null;
  status: TestRunStatus;
  executionId: string;
  executionNr: number;
  externalRunId: string;
  externalRunUrl: string | null;
}

/** Was das Abbrechen eines Pipeline-Durchlaufs zurueckmeldet (NEX-1235). */
export interface CancelledPipeline {
  runId: string;
  runKey: string | null;
  status: TestRunStatus;
  executionId: string;
  executionNr: number;
  executionStatus: ResultStatus;
  finishedAt: string | null;
}

/** Kommentar an einem Schritt-Ergebnis (NEX-1041). */
export interface TestStepComment {
  id: string;
  stepResultId: string;
  body: string;
  authorUserId: string | null;
  authorName: string | null;
  createdAt: string;
}

/**
 * Ein Schritt im Runner (NEX-1041). `actualStatus` ist das Ist-Ergebnis, das
 * die Ausfuehrenden setzen; `version` traegt das Optimistic Locking des
 * Backends und wandert bei einem Konflikt in den 409.
 */
export interface RunnerStep {
  id: string;
  testStepId: string | null;
  parentStepResultId: string | null;
  kind: StepResultKind;
  orderIndex: number;
  iterationIndex: number | null;
  iterationValues: Record<string, string> | null;
  /** Fehlt, wenn der Schritt keinen Titel hat oder der Durchlauf vor dem Titel-Snapshot entstand. */
  title?: string;
  description: string | null;
  expected: string | null;
  actualResult: string | null;
  actualStatus: ResultStatus;
  note: string | null;
  linkedBugKey: string | null;
  /** Die Anbindung, in der der verknuepfte Bug angelegt wurde; fehlt im Bestand und bei von Hand eingetragenen Schluesseln (NEX-1224). */
  linkedBugIntegrationId?: string;
  executedAt: string | null;
  durationMs: number | null;
  version: number;
  attachments: TestStepResultAttachment[];
  comments: TestStepComment[];
  defects?: DefectRef[];
}

/** Ein Testfall im Runner mit seinen Schritten in Ausfuehrungsreihenfolge. */
export interface RunnerCase {
  id: string;
  testCaseId: string;
  orderIndex: number;
  title: string | null;
  key: string | null;
  status: ResultStatus;
  startedAt: string | null;
  completedAt: string | null;
  /** Der am Fall-Ergebnis verknuepfte Bug-Vorgang; fehlt, solange keiner haengt (NEX-1298). */
  linkedBugKey?: string;
  /** Fehlt bei leeren Voraussetzungen und bei Durchlaeufen ohne Snapshot. */
  preconditions?: string;
  steps: RunnerStep[];
  /** Der Auftrag des Rechners zu diesem Fall (NEX-1752); fehlt bei jedem Lauf, der nicht auf einem Rechner lief. */
  runnerJob?: RunnerJob;
}

/** Ein Element der linken Reihenfolge-Spalte — Testfall, Plan oder Pipeline. */
export interface RunnerOrderElement {
  type: TestRunSourceType;
  refId: string | null;
  title: string | null;
  key: string | null;
  pipeline: string | null;
  orderIndex: number;
}

/** Ein dem Lauf zugeordnetes Testdatenblatt samt Zeilen. */
export interface RunnerDataSheet {
  id: string;
  key: string | null;
  name: string;
  target: string | null;
  columns: string[];
  rows: Record<string, string>[];
}

/**
 * Der CI-Lauf hinter einem Durchlauf (NEX-1198/1199).
 *
 * `buildNumber` fehlt nach dem Ausloesen (NEX-752), bis der Beobachter den Lauf sieht
 * und Nummer samt Lauf-Link nachtraegt (NEX-1290/NEX-1293) — `runUrl` zeigt bis dahin
 * auf die Workflow-Liste, ohne hinterlegten Link auf die Pipeline-Uebersicht des
 * Repositories. `logUrl` fehlt, wo der Anbieter keinen eigenen Protokoll-Link
 * hinterlegt hat.
 */
export interface RunnerPipeline {
  provider: string;
  pipeline: string;
  buildNumber?: string;
  runUrl: string;
  logUrl?: string;
}

/**
 * Ein Schritt, den die Pipeline eines Durchlaufs ausgefuehrt hat (NEX-1334) — bei GitHub
 * ein Job des Laufs.
 *
 * <p>Bei einem beobachteten Lauf ist das alles, was er an Schritten hinterlaesst: er traegt
 * keine Testfall-Ergebnisse, und die Punkte seiner Leiste sind seine Jobs. Die optionalen
 * Felder fehlen in der Antwort, statt `null` zu tragen — das Backend laesst leere Werte weg.
 */
export interface RunnerPipelineStep {
  id: string;
  /** Die Reihenfolge, in der der Anbieter die Jobs fuehrt; zaehlt ab eins. */
  nr: number;
  name: string;
  status: ResultStatus;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  /** Die Kennung des Jobs beim Anbieter — der Weg zurueck zu seinem Protokoll. */
  providerJobId?: string;
}

/**
 * Der Auftrag eines verbundenen Rechners hinter einem Durchlauf (NEX-1658). Alle Felder kommen
 * immer, leere als `null`: `runnerName` fehlt, wenn der Rechner geloescht ist.
 */
export interface RunnerJob {
  jobId: string;
  status: ExecutionJobStatus;
  runnerName: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

/** Vollstaendiger Zustand des Runners in einer Antwort (NEX-1041). */
export interface RunnerState {
  runId: string;
  runKey: string | null;
  runName: string | null;
  runKind: RunKind;
  environment: string | null;
  runStatus: TestRunStatus;
  executionId: string;
  nr: number;
  status: ResultStatus;
  live: boolean;
  startedAt: string;
  finishedAt?: string;
  durationMs?: number;
  stepsPassed: number;
  stepsTotal: number;
  executedByUserId: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  commentCount: number;
  /**
   * Ob die aufrufende Person Projektleitung ist. Die Antwort kommt vom Server, weil
   * sie dort an `Permission.PROJECT_ADMIN` haengt und damit auch Mandanten- und
   * Plattform-Administratoren ohne Projektmitgliedschaft einschliesst — die der
   * Rollen-Code einer Mitgliedschaft im Frontend gar nicht kennt.
   */
  callerIsLead: boolean;
  /**
   * Ob die aufrufende Person im Projekt schreiben darf (`TESTCASE_WRITE`, NEX-1236).
   * Vom Server beantwortet wie {@link RunnerState.callerIsLead} — die Berechtigung haengt
   * an der Projektmitgliedschaft und laesst sich hier nicht nachbauen.
   */
  callerCanWrite: boolean;
  pauseState: TestRunPauseState;
  /** Fehlt, wo es keinen CI-Lauf gibt — manueller Lauf, keine Pipeline, geloeschtes Repository. */
  pipeline?: RunnerPipeline;
  /** Fehlt, wo kein Rechner den Durchlauf ausgefuehrt hat — manueller Lauf, Pipeline. */
  runnerJob?: RunnerJob;
  /**
   * Die Schritte, die die Pipeline dieses Durchlaufs ausgefuehrt hat (NEX-1334). Leer bei
   * jedem von Hand gelaufenen Durchlauf.
   *
   * <p>Optional, weil die Antwort das Feld erst mit NEX-1334 fuehrt: ein Frontend, das
   * frueher steht als das Backend, faende es sonst als `undefined` vor, wo der Typ eine
   * Liste verspricht.
   */
  pipelineSteps?: RunnerPipelineStep[];
  /** Tests des Reports dieses Durchlaufs ohne Testfall; optional wie {@link RunnerState.pipelineSteps}. */
  unmatchedTests?: number;
  order: RunnerOrderElement[];
  cases: RunnerCase[];
  dataSheets: RunnerDataSheet[];
}

/**
 * Schritt-Update aus dem Runner. Nur `actualStatus` ist Pflicht; jedes
 * weggelassene Feld bleibt serverseitig stehen, statt geleert zu werden.
 */
export interface UpdateRunnerStepRequest {
  actualStatus: ResultStatus;
  actualResult?: string;
  note?: string;
  imageId?: string;
  /** Leerstring loest die Verknuepfung; weggelassen laesst den gespeicherten Schluessel stehen. */
  linkedBugKey?: string;
  /** Optional: der Server prueft die Version nur, wenn sie mitkommt. */
  expectedVersion?: number;
}

/** Neuer Kommentar an einem Schritt. */
export interface CreateStepCommentRequest {
  body: string;
}

/**
 * Pause- und Uebergabe-Zustand eines Laufs (NEX-1109/1110).
 *
 * Dieselbe Form antwortet auf `pause`, `resume`, `handover` und `take-over` und
 * steckt in {@link RunnerState} — das Banner nach einem Klick und das Banner nach
 * einem Neuladen entstehen so aus derselben Auswertung.
 *
 * `assignedToName` ist der aufgeloeste Anzeigename des Empfaengers und `null`,
 * wenn an eine Abteilung uebergeben wurde.
 */
export interface TestRunPauseState {
  runId: string;
  status: TestRunStatus;
  assignedToUserId: string | null;
  assignedToName: string | null;
  assignedToGroup: string | null;
  handoverNote: string | null;
  pausedAtStepResultId: string | null;
}

/** Empfaenger und Notiz einer Uebergabe (NEX-1109): genau eines von Person und Abteilung. */
export interface TestRunHandoverRequest {
  toUserId?: string;
  toGroup?: string;
  note?: string;
}
